// src/services/versionManager.js
/**
 * Simple service for managing app version and automatic cache clearing
 * Clears cache when a new app version is deployed
 */

import { clearCachedGuides } from './guideCacheService';

const VERSION_KEY = 'worldexplore_app_version';

/**
 * Fetch the current app version info from version.json
 * @returns {Promise<Object>} Version information
 */
const fetchAppVersionInfo = async () => {
  try {
    const response = await fetch('/version.json');
    return await response.json();
  } catch (error) {
    // Fallback version info
    return {
      version: '0.1.0',
      buildTime: new Date().toISOString(),
      build: { environment: 'unknown' },
      git: { commitHash: 'unknown' }
    };
  }
};

/**
 * Check if the app has been updated and clear cache if needed
 * @returns {Promise<Object>} Update status information
 */
export const checkVersionAndClearCache = async () => {
  try {
    const versionInfo = await fetchAppVersionInfo();
    const currentVersion = versionInfo.version;
    const storedVersion = localStorage.getItem(VERSION_KEY);
    
    if (!storedVersion || storedVersion !== currentVersion) {
      // Clear the cache for new version
      await clearCachedGuides();
      
      // Update stored version
      localStorage.setItem(VERSION_KEY, currentVersion);
      
      return {
        updated: true,
        fromVersion: storedVersion,
        toVersion: currentVersion,
        isFirstRun: !storedVersion,
        versionInfo
      };
    }
    
    return {
      updated: false,
      currentVersion,
      versionInfo
    };
  } catch (error) {
    console.warn('Version check failed:', error);
    return { updated: false, error: error.message };
  }
};

/**
 * Get the current app version info
 * @returns {Promise<Object>} Current app version information
 */
export const getCurrentVersionInfo = async () => {
  return await fetchAppVersionInfo();
};

/**
 * Get the current app version string
 * @returns {Promise<string>} Current app version
 */
export const getCurrentVersion = async () => {
  const versionInfo = await fetchAppVersionInfo();
  return versionInfo.version;
};

/**
 * Manually clear cache and update version
 * @returns {Promise<boolean>} Success status
 */
export const forceClearCacheAndUpdateVersion = async () => {
  try {
    const versionInfo = await fetchAppVersionInfo();
    await clearCachedGuides();
    localStorage.setItem(VERSION_KEY, versionInfo.version);
    return true;
  } catch (error) {
    console.error('Failed to force clear cache:', error);
    return false;
  }
};
